<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components';
import { EllipsisHorizontalIcon } from '@heroicons/vue/24/outline';
import { useEventListener } from '@vueuse/core';

defineProps<{
  id?: string;
  items: {
    label: string;
    action: () => void;
    requiresConfirm?: boolean;
    confirmKeys?: {
      titleKey?: string;
      confirmKey?: string;
      cancelKey?: string;
    };
  }[];
}>();

const { $t } = useNuxtApp();

const open = defineModel<boolean>('open', { default: false });

const confirming = ref<boolean>(false);
const ctxMenu = ref<HTMLElement>();
const pendingAction = ref<() => void>();
const pendingConfirmKeys = ref<{
  titleKey?: string;
  confirmKey?: string;
  cancelKey?: string;
}>();

const handleClose = () => {
  if (open.value) {
    open.value = false;
    confirming.value = false;
    pendingAction.value = undefined;
    pendingConfirmKeys.value = undefined;
  }
};

const toggle = () => {
  open.value = !open.value;
  if (!open.value) {
    confirming.value = false;
    pendingAction.value = undefined;
    pendingConfirmKeys.value = undefined;
  }
};

const handleItemClick = (item: {
  label: string;
  action: () => void;
  requiresConfirm?: boolean;
  confirmKeys?: {
    titleKey?: string;
    confirmKey?: string;
    cancelKey?: string;
  };
}) => {
  if (item.requiresConfirm) {
    confirming.value = true;
    pendingAction.value = item.action;
    pendingConfirmKeys.value = item.confirmKeys;
  } else {
    item.action();
    handleClose();
  }
};

const handleConfirm = () => {
  pendingAction.value?.();
  handleClose();
};

useEventListener('mousedown', (event: MouseEvent) => {
  if (!open.value) return;
  if (event.button === 2) open.value = false;
});

onUnmounted(() => {
  confirming.value = false;
  pendingAction.value = undefined;
  pendingConfirmKeys.value = undefined;
});
</script>

<template>
  <div ref="ctxMenu" v-on-click-outside="handleClose" class="relative">
    <slot name="trigger" :open="open" :toggle="toggle">
      <DesignSystemButton
        arial-label="More options"
        type="transparent"
        padding="none"
        @click.stop="toggle"
      >
        <EllipsisHorizontalIcon class="!h-8 !w-8" />
      </DesignSystemButton>
    </slot>

    <Transition name="fade">
      <div
        v-if="open"
        :id="id"
        class="z-5 text-base-priority rounded-xl bg-light-50 dark:bg-dark-700 shadow-xl pr-2 pl-4 py-2 absolute -bottom-22 right-0"
        :class="{ 'min-w-[300px]': confirming }"
      >
        <div v-if="!confirming" class="flex flex-col gap-2">
          <DesignSystemButton
            v-for="(item, index) in items"
            :key="index"
            class="capitalize text-xs truncate"
            type="transparent"
            padding="none"
            @click.stop="handleItemClick(item)"
          >
            {{ $t(item.label) }}
          </DesignSystemButton>
        </div>

        <div v-else class="flex flex-col gap-2 text-xs">
          <span>
            {{
              $t(pendingConfirmKeys.titleKey || 'context-menu.confirm-title')
            }}
          </span>
          <div class="flex items-center justify-center gap-2">
            <DesignSystemButton
              type="tertiary-alternative"
              class="w-full items-center justify-center text-xs"
              padding="wide-short"
              rounding="md"
              @click.stop="confirming = false"
            >
              {{
                $t(
                  pendingConfirmKeys.cancelKey || 'context-menu.confirm-cancel',
                )
              }}
            </DesignSystemButton>
            <DesignSystemButton
              class="w-full items-center justify-center text-xs"
              padding="wide-short"
              rounding="md"
              @click.stop="handleConfirm"
            >
              {{ $t(pendingConfirmKeys.confirmKey || 'context-menu.confirm') }}
            </DesignSystemButton>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  @apply transition-all duration-200 ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  @apply opacity-0 transform scale-75;
}
</style>
